/** Content sourced from https://dev.mosida.com/privacy-policy */

const PrivacyPolicyContent = () => (
  <div className="legalDocumentContent">
    <h1>MOSIDA PRIVACY POLICY</h1>
    <p>
      <strong>Effective Date:</strong> February 19, 2026
    </p>

    <h2>1. General Privacy Policy Matters</h2>
    <p>
      At MOSIDA (Museum of Social Interactive Digital Art), we take privacy seriously. This
      Privacy Policy is part of our Terms of Use and explains how we collect, use, and share
      information about you in connection with your use of our websites, emails, ticketing
      services, onsite interactive exhibits, kiosks, and any mobile applications
      (collectively, the “Service”).
    </p>
    <p>
      The terms “MOSIDA,” “we,” “us,” or “our” refer to MOSIDA LLC (Florida) and its
      affiliates. When you use the Service, you consent to the collection, use, and
      disclosure of information as described in this Privacy Policy, including information
      that may be considered personal data.
    </p>

    <h2>2. Information We Collect</h2>
    <p>We collect and store information from or about you:</p>
    <h3>a) Information you provide directly</h3>
    <p>You may provide information when you:</p>
    <ul>
      <li>
        Purchase tickets, make reservations, or transact in our shop (e.g., name, email,
        phone, billing details, transaction history).
      </li>
      <li>
        Create an account or sign up for newsletters (e.g., name, email address).
      </li>
      <li>
        Contact customer support (e.g., contact details and the content of your
        communications).
      </li>
      <li>
        Participate in surveys, promotions, contests, or events (e.g., your responses and
        contact info).
      </li>
      <li>
        Submit content through MOSIDA experiences (e.g., photos/selfies, text prompts, voice
        input, drawings, feedback, or other user-submitted content).
      </li>
    </ul>
    <h3>b) Information collected automatically</h3>
    <p>When you use the Service, we may automatically collect:</p>
    <ul>
      <li>
        Activity data (e.g., pages visited, time and date of usage, search activity,
        clicks).
      </li>
      <li>
        Device and network data (e.g., IP address, device type, operating system, browser
        type, settings).
      </li>
      <li>
        Approximate location (derived from IP address), and precise location if you enable
        it on a mobile device (you can disable location services in your device settings).
      </li>
      <li>
        Email engagement data (e.g., whether you opened or clicked an email).
      </li>
      <li>
        Log and security data (e.g., troubleshooting and fraud-prevention signals).
      </li>
      <li>
        Information collected via cookies and similar technologies, explained in Section 4.
      </li>
    </ul>
    <h3>c) Information from other sources</h3>
    <p>We may receive information from:</p>
    <ul>
      <li>
        Sign-in accounts (if you sign in via Google, Apple, Facebook, etc., depending on
        what you choose to share).
      </li>
      <li>
        Service providers that help us operate the Service (e.g., ticketing vendors, payment
        processors, analytics providers, customer support tools, marketing platforms).
      </li>
    </ul>
    <h3>d) Onsite interactive exhibit data (important for MOSIDA)</h3>
    <p>
      MOSIDA may offer exhibits that collect or generate content as part of the experience.
      Depending on the installation, we may collect:
    </p>
    <ul>
      <li>Photos or video (e.g., selfies, booth captures, group photos).</li>
      <li>Audio (e.g., voice prompts if you choose to speak).</li>
      <li>
        Motion / gesture interaction data (e.g., hand tracking or body movement used to
        control an exhibit).
      </li>
      <li>
        Experience metadata (e.g., which station you used, timestamps, selected options).
      </li>
      <li>
        AI-generated outputs (e.g., stylized portraits, caricatures, scenes, collages, or
        videos created from your inputs).
      </li>
    </ul>
    <p>
      Note: MOSIDA does not require you to submit sensitive information to enjoy the museum.
      If an exhibit offers optional advanced features, you can decline participation.
    </p>

    <h2>3. How We Use Your Information</h2>
    <p>We use information about you to:</p>
    <ul>
      <li>Operate, maintain, and improve the Service and visitor experience.</li>
      <li>Process ticket purchases, reservations, and retail transactions.</li>
      <li>
        Deliver exhibits, interactive features, AI art outputs, and onsite printing (when
        offered).
      </li>
      <li>Provide customer support and respond to inquiries.</li>
      <li>
        Troubleshoot, secure, and protect our systems; prevent fraud or misuse.
      </li>
      <li>
        Communicate with you about tickets, events, updates, and administrative notices.
      </li>
      <li>Send marketing communications (you can opt out anytime).</li>
      <li>
        Conduct surveys, promotions, sweepstakes, and administer related awards.
      </li>
      <li>
        Understand visitor engagement and improve exhibits, layouts, staffing, and
        operations.
      </li>
      <li>
        Create aggregated or anonymized analytics to evaluate how the Service is used.
      </li>
      <li>Develop and measure advertising, promotions, and marketing strategy.</li>
      <li>Comply with legal obligations and enforce our Terms.</li>
    </ul>

    <h2>4. Cookies and Similar Technologies</h2>
    <h3>a) What are cookies?</h3>
    <p>
      Cookies are small text files placed on your device that help websites recognize you
      and remember preferences. Cookies can be “persistent” or “session” cookies.
    </p>
    <h3>b) Other technologies</h3>
    <p>We may use cookies, pixels, tags, and similar tools to:</p>
    <ul>
      <li>Remember preferences and settings</li>
      <li>Understand traffic and usage patterns</li>
      <li>Measure campaign performance</li>
      <li>Detect fraud and maintain security</li>
      <li>Improve site functionality and visitor experience</li>
    </ul>
    <p>
      We may use third-party analytics providers to help us understand how users interact
      with the Service.
    </p>
    <h3>c) Your choices</h3>
    <p>
      Most browsers allow you to control cookies through settings. If you block cookies,
      parts of the Service may not function properly.
    </p>

    <h2>5. How We Share Information</h2>
    <p>We may share information about you with:</p>
    <h3>a) Service providers</h3>
    <p>
      We share data with vendors that help operate the Service (ticketing, payments,
      analytics, hosting, customer support, email/SMS). They are required to protect the
      data and only use it for contracted services.
    </p>
    <h3>b) Business partners and affiliates</h3>
    <p>
      We may share information with partners where necessary to provide services you request
      (e.g., joint promotions). Partners must agree to confidentiality and security
      obligations.
    </p>
    <h3>c) Advertising partners (if used)</h3>
    <p>
      If MOSIDA uses advertising networks, they may collect or receive information to
      measure and deliver interest-based ads. You can opt out as described in Section 10.
    </p>
    <h3>d) Legal, safety, and enforcement</h3>
    <p>
      We may disclose information if we believe in good faith it is necessary to comply with
      legal process; protect rights, safety, and property of MOSIDA, visitors, staff, and
      the public; or prevent fraud, security incidents, or misuse.
    </p>
    <h3>e) Business transfers</h3>
    <p>
      If MOSIDA is involved in a merger, acquisition, financing, reorganization, or sale of
      assets, information may be transferred as part of that transaction, subject to this
      Privacy Policy.
    </p>
    <h3>f) Anonymized / aggregated data</h3>
    <p>
      We may create and share aggregated or anonymized data that does not reasonably identify
      you.
    </p>

    <h2>6. Social Media Features and Widgets</h2>
    <p>
      The Service may include social sharing features (e.g., “share” buttons). These features
      may collect your IP address and set cookies. Your interaction with them is governed by
      the privacy policy of the social platform.
    </p>

    <h2>7. “Do Not Track” Signals</h2>
    <p>
      Some browsers send “Do Not Track” signals. The Service does not currently respond to
      them with uniform behavior changes.
    </p>

    <h2>8. Controlling Your Personal Data</h2>
    <p>
      Depending on where you live, you may have rights to access, correct, delete, or object
      to certain processing.
    </p>
    <ul>
      <li>
        You can unsubscribe from marketing emails via the link in those emails.
      </li>
      <li>You can control cookies in your browser settings.</li>
      <li>
        You can request access or deletion by contacting us (Section 16).
      </li>
    </ul>
    <p>
      Public postings: If you choose to make something public (e.g., posting a review or
      sharing content publicly), we cannot control how others use it.
    </p>

    <h2>9. Data Retention</h2>
    <p>We retain personal data only as long as reasonably necessary for:</p>
    <ul>
      <li>Ticketing and transaction records</li>
      <li>Customer support and operational needs</li>
      <li>Security, fraud prevention, and legal compliance</li>
      <li>Dispute resolution and enforcing agreements</li>
    </ul>
    <p>
      If you request deletion, we may retain certain information where required by law or
      necessary for legitimate business purposes (e.g., fraud prevention, accounting
      requirements, ongoing disputes). We may retain anonymized/aggregated data longer.
    </p>

    <h2>10. Interest-Based Advertising Opt-Out (if applicable)</h2>
    <p>
      If MOSIDA uses interest-based advertising partners, you can opt out using industry
      tools such as the Network Advertising Initiative opt-out page and the Digital
      Advertising Alliance opt-out page. Exact options depend on the partners in use.
    </p>

    <h2>11. Children</h2>
    <p>
      The Service is not directed to children under 16, and we do not knowingly collect
      personal data from children under 16 without parental consent. If you believe a child
      has provided personal data, contact us and we will take steps to remove it where
      appropriate.
    </p>

    <h2>12. Security</h2>
    <p>
      We use commercially reasonable safeguards to protect information. No method of
      transmission or storage is 100% secure, so we cannot guarantee absolute security.
    </p>

    <h2>13. Location of Data and International Transfers</h2>
    <p>
      MOSIDA is based in the United States. Your data may be processed and stored in the U.S.
      and other countries where our service providers operate. Where required, we use
      contractual safeguards designed to protect personal data.
    </p>

    <h2>14. EEA / UK Users (GDPR Notice)</h2>
    <p>
      If you are in the EEA/UK, MOSIDA may act as a Controller for account, ticketing, and
      website usage data, and may act as a Processor for certain user-generated content
      depending on context. You may have rights to access, correct, delete, restrict
      processing, object, or data portability. Contact us to exercise these rights.
    </p>

    <h2>15. Changes to this Privacy Policy</h2>
    <p>
      We may update this Privacy Policy periodically. The “Effective Date” above indicates
      the latest revision. Continued use of the Service after changes means you accept the
      revised policy.
    </p>

    <h2>16. Contact Information</h2>
    <p>If you have questions or requests regarding privacy, contact:</p>
    <p>
      MOSIDA
      <br />
      Attn: Privacy / Legal
      <br />
      Miami, FL (Wynwood)
      <br />
      Email: info@mosida.com
    </p>
  </div>
);

export default PrivacyPolicyContent;
